import { fetchContentfulByHandle } from 'connectors/contentful';
import { Entry } from 'contentful';
import { usePetContext } from 'contexts/pet';
import { useRouteContext } from 'contexts/route';
import { IProductPagesFields } from 'global';
import { useContentful } from 'modules/content-cms/components/context';
import { StaticPDPProduct } from 'modules/product-details/types';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isCollectionPage as _isCollectionPage } from 'utils/collection';
import { isLegacyProductPageModel } from 'utils/contentful';

export interface Breadcrumbs {
  breadcrumbList: {
    element: string;
    href: string;
    as?: string;
  }[];
  isLoading: boolean;
}

/**
 * It generates the data used by the Breadcrumbs component on PDPs and CDPs.
 * @param {StaticPDPProduct} product
 */
export const useBreadcrumbs = ({
  product,
}: {
  product: StaticPDPProduct | undefined;
}): Breadcrumbs => {
  const { locale } = useRouter();
  const { data: contentful } = useContentful();
  const intl = useIntl();
  const { current } = usePetContext();
  const { previousPath } = useRouteContext();

  const isPreviousPathCollectionPage = useMemo(
    () => previousPath?.includes('collection'),
    [previousPath]
  );

  const [previousPathCollectionTitle, setPreviousPathCollectionTitle] =
    useState<string | undefined>(undefined);

  const [data, setData] = useState<Entry<IProductPagesFields> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const path =
          isPreviousPathCollectionPage && previousPath
            ? previousPath.split('/').slice(1, 3).join('/')
            : null;
        if (path) {
          const response = await fetchContentfulByHandle({
            handle: path,
            locale,
          });
          setData(response);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isPreviousPathCollectionPage, locale, previousPath]);

  useEffect(() => {
    if (!isLoading) {
      const newTitle =
        data?.fields.breadcrumbsCollectionTitle ?? data?.fields.headline;
      if (newTitle !== previousPathCollectionTitle) {
        setPreviousPathCollectionTitle(newTitle);
      }
    }
  }, [data, isLoading, previousPathCollectionTitle]);
  const isCollectionPage = useMemo(
    () => _isCollectionPage(contentful),
    [contentful]
  );

  const breadcrumbsCollectionHandle = useMemo(() => {
    if (!isCollectionPage) {
      return isPreviousPathCollectionPage && previousPath
        ? previousPath.substring(1)
        : contentful.fields.mainCollection?.fields.handle;
    } else {
      return contentful.fields.handle;
    }
  }, [
    contentful,
    isCollectionPage,
    isPreviousPathCollectionPage,
    previousPath,
  ]);

  const breadcrumbsCollectionTitle = useMemo(() => {
    if (!isCollectionPage) {
      return isPreviousPathCollectionPage && previousPath
        ? previousPathCollectionTitle
        : (contentful.fields.mainCollection?.fields
            .breadcrumbsCollectionTitle ??
            // needed as a fallback value for missing breadcrumbs collection titles
            contentful.fields.headline);
    } else if (isLegacyProductPageModel(contentful)) {
      return (
        contentful.fields.breadcrumbsCollectionTitle ??
        // needed as a fallback value for missing breadcrumbs collection titles
        contentful.fields.headline
      );
    }
  }, [
    contentful,
    isCollectionPage,
    isPreviousPathCollectionPage,
    previousPath,
    previousPathCollectionTitle,
  ]);

  const breadcrumbList = useMemo(() => {
    const _breadcrumbs = [
      {
        element:
          current === 'dogs'
            ? intl.formatMessage({ id: 'common:dogs' })
            : intl.formatMessage({ id: 'common:cats' }),
        href: `/pages/${
          current === 'dogs'
            ? intl.formatMessage({ id: 'common:dogs' }).toLowerCase()
            : intl.formatMessage({ id: 'common:cats' }).toLowerCase()
        }`,
      },
    ];

    if (breadcrumbsCollectionHandle && breadcrumbsCollectionTitle) {
      _breadcrumbs.push({
        element: breadcrumbsCollectionTitle,
        href: `/${breadcrumbsCollectionHandle}`,
      });
    }

    if (product) {
      _breadcrumbs.push({
        element: product.metadata.accentuate.mainTitle || product.title,
        href: `/${contentful.fields.handle}`,
      });
    }

    return _breadcrumbs;
  }, [
    product,
    current,
    intl,
    breadcrumbsCollectionTitle,
    breadcrumbsCollectionHandle,
    contentful,
  ]);

  return { breadcrumbList, isLoading };
};
